/**
 * RecipeGallery — queue #664 §UAT-2026-07-09-R664 §R664-3.
 *
 * 레시피 뷰 기본 화면 — 6 레시피 카드 그리드(카드=이름 + 1줄 목적 + 구성 모듈 코드 배지 목록 +
 * 스텝 수). 카드 클릭 시 호출부(CommonShowcasePage)가 `?recipe=<id>` 로 상세 전환한다.
 */
import { useTranslation } from 'react-i18next';
import type { Recipe } from '../data/recipes';

interface Props {
  recipes: Recipe[];
  onSelect: (id: string) => void;
}

export function RecipeGallery({ recipes, onSelect }: Props) {
  const { t } = useTranslation();

  return (
    <section aria-label={t('showcase.recipe.gallery.ariaLabel')}>
      <h2
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 var(--spacing-1) 0',
        }}
      >
        {t('showcase.recipe.gallery.title')}
      </h2>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          margin: '0 0 var(--spacing-4) 0',
        }}
      >
        {t('showcase.recipe.gallery.subtitle')}
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 'var(--spacing-4)',
        }}
      >
        {recipes.map((recipe) => (
          <button
            key={recipe.id}
            type="button"
            data-testid={`recipe-card-${recipe.id}`}
            onClick={() => onSelect(recipe.id)}
            style={{
              textAlign: 'left',
              padding: 'var(--spacing-4)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-background)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-2)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-base)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
              }}
            >
              {t(recipe.nameKey)}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t(recipe.summaryKey)}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)' }}>
              {recipe.modules.map((m) => (
                <code
                  key={m.code}
                  style={{
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 'var(--font-size-xs)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    padding: '2px var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-brand-primary)',
                  }}
                >
                  {m.code}
                </code>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-3)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              <span>{t('showcase.recipe.card.moduleCount', { count: recipe.modules.length })}</span>
              <span>{t('showcase.recipe.card.stepCount', { count: recipe.steps.length })}</span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
